import { Fragment, type ReactNode } from 'react'

import { getCourtoisUrl } from '@/services/courtois'
import type { DastNode } from '../types'

// Changelog bodies are authored on the marketing site, so their links are
// site-relative. Without this they would resolve against the app domain.
const resolveUrl = (url: string): string =>
  url.startsWith('/') ? getCourtoisUrl(url) : url

const MARK_WRAPPERS: Record<string, (child: ReactNode) => ReactNode> = {
  strong: (child) => <strong>{child}</strong>,
  emphasis: (child) => <em>{child}</em>,
  underline: (child) => <u>{child}</u>,
  strikethrough: (child) => <s>{child}</s>,
  code: (child) => <code>{child}</code>,
}

const renderInline = (node: DastNode, key: number): ReactNode => {
  if (node.type === 'span') {
    let content: ReactNode = node.value ?? ''
    for (const mark of node.marks ?? []) {
      content = MARK_WRAPPERS[mark]?.(content) ?? content
    }
    return <Fragment key={key}>{content}</Fragment>
  }
  if (node.type === 'link') {
    return (
      <a
        key={key}
        href={resolveUrl(node.url ?? '')}
        target="_blank"
        rel="noreferrer"
      >
        {(node.children ?? []).map(renderInline)}
      </a>
    )
  }
  return null
}

const renderBlock = (node: DastNode, key: number): ReactNode => {
  const children = node.children ?? []
  switch (node.type) {
    case 'paragraph':
      return (
        <p key={key} className="text-pretty">
          {children.map(renderInline)}
        </p>
      )
    case 'heading': {
      const Heading = `h${node.level ?? 2}` as 'h1'
      return (
        <Heading key={key} className="text-pretty">
          {children.map(renderInline)}
        </Heading>
      )
    }
    case 'list': {
      const List = node.style === 'numbered' ? 'ol' : 'ul'
      return <List key={key}>{children.map(renderBlock)}</List>
    }
    case 'listItem':
      return (
        <li key={key} className="text-pretty">
          {children.map((child, index) =>
            child.type === 'paragraph'
              ? (child.children ?? []).map(renderInline)
              : renderBlock(child, index),
          )}
        </li>
      )
    case 'blockquote':
      return (
        <blockquote key={key} className="text-pretty">
          {children.map(renderBlock)}
        </blockquote>
      )
    case 'thematicBreak':
      return <hr key={key} />
    case 'code':
      return (
        <pre key={key}>
          <code>{node.code}</code>
        </pre>
      )
    default:
      return <Fragment key={key}>{children.map(renderBlock)}</Fragment>
  }
}

export const StructuredText = ({ document }: { document: DastNode }) => (
  <div className="prose prose-sm max-w-none text-blue-7">
    {(document.children ?? []).map(renderBlock)}
  </div>
)
